import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Inbox, Mail, Trash2, MailOpen, CheckCircle2, CornerUpLeft } from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ContactInbox() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["contact-messages"],
    queryFn: async () => (await api.get("/admin/contact-messages")).data,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["contact-messages"] });

  const readMut = useMutation({
    mutationFn: async (id) => (await api.put(`/admin/contact-messages/${id}/read`)).data,
    onSuccess: invalidate,
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/contact-messages/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success("Message deleted"); },
  });

  const messages = data?.messages || [];
  const unread = data?.unread || 0;

  if (isLoading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="contact-inbox">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Admin</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3">
          <Inbox className="w-8 h-8 text-primary" /> Contact Inbox
        </h1>
        <p className="text-muted-foreground mt-1">
          Enquiries from the website contact form.{" "}
          {unread > 0 && <span className="text-primary font-medium" data-testid="unread-count">{unread} unread</span>}
        </p>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md text-muted-foreground" data-testid="inbox-empty">
          No enquiries yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="messages-list">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`p-5 rounded-md border bg-card ${m.read ? "border-border" : "border-primary/50"}`}
              data-testid={`message-${m.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!m.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" data-testid={`unread-dot-${m.id}`} />}
                    <span className="font-heading font-bold">{m.name}</span>
                    <a href={`mailto:${m.email}`} className="text-sm text-primary hover:underline">{m.email}</a>
                  </div>
                  {m.topic && <span className="inline-block mt-1.5 text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-secondary text-muted-foreground">{m.topic}</span>}
                </div>
                <span className="text-xs font-mono text-muted-foreground shrink-0">{fmt(m.created_at)}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mt-3 whitespace-pre-wrap">{m.message}</p>
              <div className="flex gap-2 mt-4">
                <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.topic || "Your EliteHire India enquiry")}`}>
                  <Button size="sm" className="rounded-md gap-1.5" data-testid={`reply-${m.id}`}>
                    <CornerUpLeft className="w-3.5 h-3.5" /> Reply
                  </Button>
                </a>
                <Button size="sm" variant="outline" className="rounded-md gap-1.5" onClick={() => readMut.mutate(m.id)} disabled={readMut.isPending} data-testid={`toggle-read-${m.id}`}>
                  {m.read ? <><Mail className="w-3.5 h-3.5" /> Mark unread</> : <><MailOpen className="w-3.5 h-3.5" /> Mark read</>}
                </Button>
                <Button size="sm" variant="ghost" className="rounded-md gap-1.5 text-muted-foreground hover:text-destructive ml-auto" onClick={() => delMut.mutate(m.id)} disabled={delMut.isPending} data-testid={`delete-${m.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
